import React, { useRef, useEffect, useMemo } from "react";
import { Chart, registerables } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import ItalianMapChart from "./ItalianMapChart";
Chart.register(...registerables);
Chart.register(ChartDataLabels);

// Opacità massima della sfumatura disegnata sotto la linea dei grafici temporali
const AREA_GRADIENT_OPACITY = 0.35;

// Funzione che riscrive un colore in formato rgba applicandogli una certa opacità.
// Ritorna null sui colori che non è in grado di interpretare (es. 'transparent'), così da lasciare
// il rispettivo dataset invariato
const colorWithOpacity = (color, opacity) => {
  if (typeof color !== 'string') return null;

  const rgbColor = color.match(/rgba?\(([^)]+)\)/i);
  if (rgbColor) {
    const [red, green, blue] = rgbColor[1].split(/[\s,]+/).filter(value => value !== '').map(value => parseFloat(value));
    if ([red, green, blue].some(value => isNaN(value))) return null;
    return `rgba(${red}, ${green}, ${blue}, ${opacity})`;
  }

  const hexColor = color.trim().replace('#', '');
  const expandedHexColor = hexColor.length === 3 ? hexColor.split('').map(char => char + char).join('') : hexColor;
  if (!/^[0-9a-f]{6}$/i.test(expandedHexColor)) return null;

  const [red, green, blue] = [0, 2, 4].map(index => parseInt(expandedHexColor.slice(index, index + 2), 16));
  return `rgba(${red}, ${green}, ${blue}, ${opacity})`;
}

// Funzione che riempie l'area sottostante la linea di un grafico temporale con una sfumatura verticale
// dello stesso colore della linea, che sfuma fino a diventare del tutto trasparente sull'asse delle ascisse
const buildAreaGradient = (color) => (context) => {
  const { ctx, chartArea } = context.chart;

  // Al primo render il chartArea non è ancora calcolato: Chart.js richiama la funzione appena lo conosce
  if (!chartArea) return 'transparent';

  const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
  gradient.addColorStop(0, colorWithOpacity(color, AREA_GRADIENT_OPACITY));
  gradient.addColorStop(1, colorWithOpacity(color, 0));
  return gradient;
}

const defaultOptions = {
  scales: {
    y: {
      beginAtZero: true
    }
  },
  plugins: {
    legend: {
      display: false
    },
    datalabels: {
      display: false
    }
  }
};

const LineChart = ({
  type,
  datasets,
  labels,
  maxHeight,
  options,
  id,
  areaGradient = true
}) => {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  const chartType = type || 'line';
  const chartId = id || 'myChart';
  const resolvedOptions = options || defaultOptions;

  // Sui soli grafici a linea riempio l'area sotto la linea con una sfumatura del colore della linea stessa, a meno
  // che chi usa il componente non la escluda (areaGradient: con più linee le sfumature si sovrappongono)
  const resolvedDatasets = useMemo(() => {
    if (chartType !== 'line' || !areaGradient) return datasets;

    return datasets.map(dataset => {
      if (!colorWithOpacity(dataset.borderColor, AREA_GRADIENT_OPACITY)) return dataset;

      return {
        ...dataset,
        fill: true,
        backgroundColor: buildAreaGradient(dataset.borderColor)
      };
    });
  }, [datasets, chartType, areaGradient]);

  useEffect(() => {
    if (chartType === 'italian_map') return;
    if (!canvasRef.current) return;

    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    chartRef.current = new Chart(ctx, {
      type: chartType,
      data: {
        labels: [].concat(...labels),
        datasets: resolvedDatasets
      },
      options: {
        ...resolvedOptions,
        responsive: true,
        maintainAspectRatio: false
      }
    });

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [chartType, chartId]);

  useEffect(() => {
    if (!chartRef.current) return;

    chartRef.current.data.labels = [].concat(...labels);

    if (chartRef.current.data.datasets.length === resolvedDatasets.length) {
      chartRef.current.data.datasets.forEach((ds, i) => {
        if (resolvedDatasets[i]) {
          Object.assign(ds, resolvedDatasets[i]);
        }
      });
    } else {
      chartRef.current.data.datasets = resolvedDatasets;
    }

    chartRef.current.update('none');
  }, [resolvedDatasets, labels]);

  if (chartType === 'italian_map') {
    return (
      <div className="h-100">
        <ItalianMapChart
          datasets={datasets}
          maxHeight={maxHeight}
        />
      </div>
    );
  }

  return (
    <div className="h-100">
      <canvas ref={canvasRef} id={chartId} className="w-100" style={{ maxHeight: maxHeight || 'auto' }} />
    </div>
  );
}

export default React.memo(LineChart);
